import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchReward,
  fetchRewards,
  createReward,
  updateReward,
  destroyReward
} from "src/actions/mine_craft/rewardsActions";

export interface RewardInterface {
  id: number;
  challenge_id: number;
  title: string;

  thumbnail_url: string;

  coins: number;

  from: number;
  to: number;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: RewardInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<RewardInterface>();

const slice = createSlice({
  name: 'rewards',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchRewards.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchRewards.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.rewards;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchRewards.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createReward.fulfilled, (state, action) => {
        state.newParams = initialState.newParams;
        state.newError  = initialState.newError;

        const reward = Object.values(action.payload.reward)[0];
        state.entities[reward.id] = reward;
        state.ids = [...state.ids, reward.id];
      })
      .addCase(createReward.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchReward.fulfilled, (state, action) => {
        const reward = Object.values(action.payload.reward)[0];
        state.entities[reward.id] = reward;
      })

      // #update
      .addCase(updateReward.fulfilled, (state, action) => {
        state.editError  = initialState.editError;
        const reward = Object.values(action.payload.reward)[0];
        state.entities[reward.id] = reward;
      })
      .addCase(updateReward.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyReward.fulfilled, (state, action) => {
        delete state.entities[action.payload.id];
        state.ids = state.ids.filter(id => id !== action.payload.id);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectRewardById,
  selectIds: selectRewardIds,
  selectEntities: selectRewardEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.rewards);
